import { useCallback, useEffect, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import type { AxiosError } from 'axios';
import type { User } from '@auth0/auth0-spa-js';
import type { ApplyRequest, UseChatState } from './useChat';
import type { ChatMessage, ChatMode } from './types';
import { ensureChatExists, sendResearchMessage, sendEditMessage } from '../api/messages';
import { uploadDocxToS3, downloadDocxFromS3 } from '../api/s3';
import {
  applyQuoteEdit,
  computeDocumentHash,
  getSelectedText,
  insertHtmlAtCursor,
  readCurrentDocumentBytes,
} from '../office/documentIO';
import { applyEditedDocument } from '../office/ooxmlRedline';
import { stampDraftProvenance } from '../office/provenance';
import {
  getWordEditState,
  pushSnapshot,
  setWordEditState,
  popSnapshot,
} from '../office/documentSettings';
import { truncate } from '../lib/utils';

interface Params {
  user: User;
  mode: ChatMode;
  chat: UseChatState;
}

export type EditStage = 'idle' | 'uploading' | 'sending' | 'applying';

/**
 * Owns the full send flow:
 *   - Research:  ensureChatExists (once per thread) → POST /chat/messages
 *   - Edit:      ensureChatExists + S3 upload + POST /chat/messages
 *                then listens for ApplyRequest from useChat and applies the
 *                returned edit to the Word document (full_edit or quote_edit).
 *
 * Parent passes the useChat result in `chat`; this hook calls chat.* helpers
 * to manipulate messages and subscribes to chat.onApplyRequest for edit
 * responses.
 */
export function useSendMessage({ user, mode, chat }: Params) {
  const [editStage, setEditStage] = useState<EditStage>('idle');

  // A1: Track which chat_ids have already had their backend row created.
  // Prevents the "duplicate key value violates unique constraint" 500 on
  // follow-up messages (web app only calls POST /api/v1/chat once per thread).
  const chatsCreatedRef = useRef<Set<string>>(new Set());

  // C3: When we send a quote_edit, store (changeId → originalText) so when
  // the SSE response comes back we can look up the original for applying
  // the tracked change at the right range.
  const pendingQuoteEditsRef = useRef<Map<string, string>>(new Map());

  const createChatOnce = useCallback(
    async (args: {
      chatId: string;
      chatType: 'research' | 'draft';
      name: string;
      metadata?: Record<string, unknown>;
    }) => {
      if (chatsCreatedRef.current.has(args.chatId)) return;
      try {
        await ensureChatExists(args);
        chatsCreatedRef.current.add(args.chatId);
      } catch (err) {
        // If backend returns duplicate-key (chat exists already), treat as
        // success. Any other error propagates.
        const status = (err as AxiosError | undefined)?.response?.status;
        const message =
          (err as AxiosError<{ message?: string }> | undefined)?.response?.data?.message ?? '';
        if (status === 409 || /duplicate key|already exists/i.test(message)) {
          chatsCreatedRef.current.add(args.chatId);
          return;
        }
        throw err;
      }
    },
    [],
  );

  const insertAnswerIntoDocument = useCallback(async (text: string) => {
    // Minimal markdown → HTML so answer pastes in as paragraphs rather than
    // one run-on line. For phase 4 we can pipe through the full MarkdownRenderer.
    const html = text
      .replace(/\n\n+/g, '</p><p>')
      .replace(/\n/g, '<br/>')
      .replace(/^(.*)$/, '<p>$1</p>');
    await insertHtmlAtCursor(html);
  }, []);

  const revert = useCallback(async () => {
    const bytes = await popSnapshot();
    if (!bytes) return false;
    await applyEditedDocument(bytes);
    await setWordEditState({ lastUploadHash: await computeDocumentHash() });
    return true;
  }, []);

  // ---------------- Apply-request handler (full_edit + quote_edit) ----------------

  useEffect(() => {
    const unsubscribe = chat.onApplyRequest(async (req: ApplyRequest) => {
      if (req.kind === 'full_edit') {
        setEditStage('applying');
        try {
          // ----- Apply the server's tracked-changes DOCX -----
          // The AI tracked-editor writes the edited DOCX to S3 with every
          // change already marked up as w:ins/w:del authored "NyayAssist
          // AI". We download those bytes and splice them into the open
          // document via applyEditedDocument (surgical OOXML diff, with a
          // wholesale-replace fallback) — both paths PRESERVE the server's
          // w:ins/w:del markup. The per-edit Accept/Reject cards then
          // enumerate those tracked changes directly from the document
          // (EditOperationsReview → readPendingAiEdits), so no server-side
          // edit_operations metadata is needed here.
          //
          // We deliberately do NOT re-apply raw edit_operations client-side
          // anymore: that path (removed 2026-06-19) re-matched each op with
          // a weak paragraph-level body.search() that failed on documents
          // with many identical anchors (e.g. repeated "_____" blanks),
          // dropping edits the server had already applied cleanly. The
          // server's matcher is the single source of truth.
          const [editedBuf, currentBytes] = await Promise.all([
            downloadDocxFromS3(req.s3Key),
            readCurrentDocumentBytes().catch(() => undefined),
          ]);
          const editedBytes = new Uint8Array(editedBuf);
          const result = await applyEditedDocument(editedBytes, currentBytes);
          await setWordEditState({
            currentS3Key: req.s3Key,
            lastUploadHash: await computeDocumentHash(),
            // Phase E — persist the server's version + SHA-256 so on next boot
            // we can detect whether a newer version exists elsewhere.
            lastAppliedVersion: req.latestVersion ?? null,
            lastAppliedSha256: req.docxSha256 ?? null,
            // Reset dismissals — this IS the latest version now.
            dismissedUpdateKeys: [],
          });
          // Phase F — belt-and-suspenders stamp: if the server-stamped bytes
          // we just inserted didn't survive Word's round-trip (can happen
          // with surgical paragraph-level inserts that skip our customXml),
          // re-stamp with the chat_id so the file stays self-describing.
          const linkedId = getWordEditState().linkedDraftChatId;
          const stampChatId = linkedId ?? chat.currentThreadId;
          if (stampChatId) {
            void stampDraftProvenance({
              chatId: stampChatId,
              draftId: stampChatId,
              version: req.latestVersion ?? '1',
            });
          }
          chat.updateMessage(req.messageId, {
            streaming: false,
            editCount: result.paragraphsChanged ?? req.editCount,
            editedS3Key: req.s3Key,
          });
          // eslint-disable-next-line no-console
          console.info('[Apply:full_edit] applied server DOCX', result);
        } catch (err) {
          chat.updateMessage(req.messageId, {
            streaming: false,
            error: err instanceof Error ? err.message : 'Failed to apply AI edits',
          });
        } finally {
          setEditStage('idle');
        }
        return;
      }

      if (req.kind === 'quote_edit') {
        setEditStage('applying');
        try {
          const originalText = pendingQuoteEditsRef.current.get(req.changeId);
          await applyQuoteEdit({ editedText: req.editedText, originalText });
          pendingQuoteEditsRef.current.delete(req.changeId);
          chat.updateMessage(req.messageId, {
            streaming: false,
            originalText,
            changeId: req.changeId,
          });
          // eslint-disable-next-line no-console
          console.info('[Apply:quote_edit] applied changeId', req.changeId);
        } catch (err) {
          chat.updateMessage(req.messageId, {
            streaming: false,
            error: err instanceof Error ? err.message : 'Failed to apply quote edit',
          });
        } finally {
          setEditStage('idle');
        }
        return;
      }
    });
    return unsubscribe;
  }, [chat]);

  // ---------------- Send flow ----------------

  const send = useCallback(
    async (content: string) => {
      const text = content.trim();
      if (!text) return;

      const userId = user.sub ?? 'unknown';
      const selection = await getSelectedText().catch(() => '');

      chat.appendUserMessage(text);

      // Reserve the AI message slot immediately so thinking/status events
      // from SSE patch THIS slot (race-free — matches web app behavior).
      const aiMessageId = chat.appendAiMessage({ streaming: true });

      if (mode === 'research') {
        const chatId = chat.currentThreadId ?? chat.startResearchThread();
        const featureId = uuid();
        // Only create the chat row for brand-new threads. Matches web's gate
        // (createChat dispatched only when messages.length === 0). For any
        // history-resumed or forked thread, the row already exists server-side.
        const isBrandNewChat = !chat.loadedChatType;
        try {
          if (isBrandNewChat) {
            await createChatOnce({
              chatId,
              chatType: 'research',
              name: truncate(text, 60),
            });
          }
          await sendResearchMessage({
            userId,
            chatId,
            featureId,
            content: text,
            quotedContent: selection || undefined,
          });
        } catch (err) {
          chat.updateMessage(aiMessageId, {
            streaming: false,
            error: err instanceof Error ? err.message : 'Failed to send research message',
          });
        }
        return;
      }

      // --- Edit mode ---
      // Unified model: every edit session lives in a `chat_type='draft'` chat
      // (just like web). If the doc is linked to an existing draft, route
      // there; otherwise start a fresh draft chat for this Word document.
      const linkedDraftChatId = getWordEditState().linkedDraftChatId ?? null;
      const chatId =
        linkedDraftChatId ?? chat.currentThreadId ?? chat.startEditThread();
      setEditStage('uploading');
      try {
        // First edit for this Word document — remember the chat we're using.
        const existing = getWordEditState();
        if (!existing.chatId || existing.chatId !== chatId) {
          await setWordEditState({ chatId });
        }
        // Detect whether this is the very first edit on a brand-new chat.
        // Signals:
        //   - no link to an existing draft chat AND
        //   - not resumed from history (loadedChatType === null) AND
        //   - we haven't already created this chat in-session
        const isBrandNewChat =
          !linkedDraftChatId &&
          !chat.loadedChatType &&
          !chatsCreatedRef.current.has(chatId);
        if (isBrandNewChat) {
          await createChatOnce({
            chatId,
            chatType: 'draft',
            name: truncate(text, 60),
            metadata: { source: 'word_addon', status: 'DraftGenerated' },
          });
        }

        // Upload DOCX to S3 only if the document changed since last upload.
        const hash = await computeDocumentHash();
        const current = getWordEditState();
        let s3Key = current.currentS3Key;
        let uploadedBytesSha: string | null = null;
        if (!s3Key || hash !== current.lastUploadHash) {
          const bytes = await readCurrentDocumentBytes();
          await pushSnapshot(bytes);
          s3Key = `draft/${userId}/${chatId}_v1_${Date.now()}.docx`;
          await uploadDocxToS3(bytes, s3Key);
          uploadedBytesSha = hash;
          await setWordEditState({ currentS3Key: s3Key, lastUploadHash: hash });
        }

        setEditStage('sending');

        // C3: for quote_edit, generate a change_id and remember the original
        // selected text. When the SSE response echoes change_id, we'll look
        // it up to re-find the range in Word for the tracked-change apply.
        const quoted = selection || undefined;
        const changeId = quoted ? uuid() : undefined;
        if (changeId && quoted) {
          pendingQuoteEditsRef.current.set(changeId, quoted);
        }

        // Version sent on the wire is what the agent increments from.
        //   - First edit in session       → '1' (we also insert draft_versions v1).
        //   - Subsequent edits            → lastAppliedVersion from document
        //                                    settings (set after the previous
        //                                    apply). Without this, every edit
        //                                    passes '1' and the agent tries
        //                                    to insert draft_versions v2 on
        //                                    each call → PK collision on the
        //                                    second edit.
        const stateForVersion = getWordEditState();
        const nextVersionBase = isBrandNewChat
          ? '1'
          : stateForVersion.lastAppliedVersion ?? '1';

        await sendEditMessage({
          userId,
          chatId,
          content: text,
          draftS3Key: s3Key,
          quotedContent: quoted,
          changeId,
          // On the brand-new-chat send, include draft + draft_versions inserts
          // so web's /draft/edit/[id] can locate the initial DOCX version.
          isFirstEdit: isBrandNewChat,
          version: nextVersionBase,
        });
        // uploadedBytesSha reserved for Phase E (outdated-copy detection).
        void uploadedBytesSha;
      } catch (err) {
        chat.updateMessage(aiMessageId, {
          streaming: false,
          error: err instanceof Error ? err.message : 'Failed to send edit',
        });
      } finally {
        setEditStage('idle');
      }
    },
    [chat, createChatOnce, mode, user],
  );

  return { send, insertAnswerIntoDocument, revert, editStage };
}
